import { observable, computed, action, decorate } from "mobx";
import * as mobx from 'mobx';
import { runInAction } from "mobx";

import * as order from '../communicator/order';
import * as tools from '../communicator/tools'

class StoreShipping {

    shippingCalculationQueue = Promise.resolve();
    pendingShippingCalculation = null;

    constructor(storeRoot) {
        this.storeRoot = storeRoot;
        this.storeAuth = this.storeRoot.storeAuth;
        this.storeUser = this.storeRoot.storeUser;
        this.storeOrder = this.storeRoot.storeOrder;
    }

    calculateShipping(orderId) {
        // Een berekening die nog wacht leest bij de start de actuele cart; extra
        // aanvragen in de tussentijd sluiten daarop aan i.p.v. een nieuwe te starten.
        if (this.pendingShippingCalculation) {
            return this.pendingShippingCalculation;
        }

        const calculate = async () => {
            this.pendingShippingCalculation = null;
            this.storeOrder.shippingCalculationState = 'loading';
            try {
                const result = await this.calculateShippingForOrder(orderId);
                this.storeOrder.shippingCalculationState = 'ready';
                return result;
            } catch (error) {
                this.storeOrder.shippingCalculationState = 'error';
                throw error;
            }
        };

        this.shippingCalculationQueue = this.shippingCalculationQueue.then(calculate, calculate);
        this.pendingShippingCalculation = this.shippingCalculationQueue;
        return this.shippingCalculationQueue;
    }

    async calculateShippingForOrder(orderId) {
        let shipping = 0;
        let weight = 0;
        let packageType = 2;

        // Guests need a quote too. Only order-specific reads/writes require an ID.
        orderId = this.storeOrder.order_data.order_id ?? orderId;
        const hasOrder = this.storeOrder.isRealOrderId(orderId);
        if (this.storeOrder.nrCartItems === 0) {
            this.storeOrder.setShippingCosts(0);
            this.storeOrder.setShippingVAT(0);
            return shipping;
        }

        const snapshot = JSON.stringify([this.storeOrder.cart, this.storeOrder.order_data.client_id,
            this.getShippingCountryCode(), this.storeOrder.order_data.order_id]);

        const userId = Number(this.storeOrder.order_data.client_id) || 0;
        const countryCode = this.getShippingCountryCode();
        const shippingAbroad = this.checkShippingCountry(countryCode);

        // De vrijstelling NoShippingCosts geldt uitsluitend voor Nederland.
        // Een Belgische bestelling moet altijd het buitenlandtarief volgen.
        const hasNoShippingCustomer = countryCode === 'NL' && this.checkNoShippingCustomer(userId);
        const hasNoShippingOrder = hasOrder && countryCode === 'NL' && await this.checkNoShippingOrder(orderId);
        const hasShippingOrderOverruled = hasOrder && this.checkShippingOrderOverruled(orderId);
        const hasNoShippingSku = this.checkNoShippingSku(orderId);

        if (hasNoShippingCustomer) {
            shipping = 0;
        }

        if (hasNoShippingOrder) {
            shipping = 0;
        }
        
        if (hasShippingOrderOverruled) {
            shipping = await this.getShippingOrderOverruled(orderId);
        }

        // Geen producten in de cart → nooit verzendkosten (de doosgewicht-basis
        // van 80g zou anders ook bij een lege cart verzendkosten opleveren, en de
        // buitenland-tak mist de totalCart<=0 guard van checkFreeShippingBoundaries).
        const hasNoItems = this.storeOrder.nrCartItems === 0;

        if (!hasNoItems && !hasNoShippingCustomer && !hasNoShippingOrder && !hasShippingOrderOverruled) {
            weight = this.getWeight();
            if (!Number.isFinite(Number(weight))) throw new Error('Geen geldig verzendgewicht.');

            if (!Object.is(weight, NaN)) {
                if (weight <= 0) {
                    // SbC only downloads
                    shipping = 0;
                } else {
                    if (shippingAbroad) {
                        if (this.storeOrder.countriesList.length === 0) {
                            await this.storeOrder.getCountries();
                        }
                        shipping = await this.getShippingCostsAbroad(weight, countryCode);
                    } else {
                        packageType = this.getPackageType(weight);

                        shipping = await this.getShippingCosts(orderId, weight, packageType);
                        shipping = this.checkFreeShippingBoundaries(orderId, shipping);
                    }
                }
            }
            
        }

        if (hasNoShippingSku) {
            shipping= 0;
        }

        const numericShipping = Number(shipping);
        if (!Number.isFinite(numericShipping) || numericShipping < 0) {
            throw new Error('Geen geldig verzendtarief voor ' + countryCode + '.');
        }

        // Do not apply a quote to a cart that changed while a tariff was loading.
        if (snapshot !== JSON.stringify([this.storeOrder.cart, this.storeOrder.order_data.client_id,
            this.getShippingCountryCode(), this.storeOrder.order_data.order_id])) {
            return this.calculateShippingForOrder(this.storeOrder.order_data.order_id ?? orderId);
        }
        const vatPerc = this.calculateShippingVat();
        runInAction(() => {
            this.storeOrder.setShippingCosts(numericShipping);
            this.storeOrder.setShippingVAT(vatPerc);
            this.storeOrder.calculateShippingCostsTotal();
        });

        /* 
         * SbC save in orderlines
         */
        if (hasOrder && !hasShippingOrderOverruled) {
            const returnData = await this.storeOrder.editOrderLineShipping();
        }

        return numericShipping;
    }

    getShippingCountryCode() {
        const orderData = this.storeOrder.order_data;
        const rawCountryCode = orderData.client_settings.has_delivery_address === true
            ? orderData.delivery_address.country_code
            : (orderData.client_profile.country_code || orderData.billing_address.country_code);
        const countryCode = String(rawCountryCode || '').trim().toUpperCase();

        return /^[A-Z]{2}$/.test(countryCode) ? countryCode : 'NL';
    }

    checkShippingCountry(countryCode = this.getShippingCountryCode()) {
        return countryCode !== 'NL';
    }

    checkNoShippingCustomer(userId) {
        const checkoutUserId = Number(userId);
        const authUserId = Number(this.storeAuth.user.user_id);
        const isSameAccount = this.storeAuth.user.is_loggedin === true
            && Number.isSafeInteger(checkoutUserId)
            && checkoutUserId > 0
            && authUserId === checkoutUserId;
        const value = this.storeAuth.user.no_shipping_costs;

        return isSameAccount && (value === true || value === 1 || value === '1' || value === 'on');
    }

    checkShippingCustomerOverruled(userId) {
        let isOverruled = false;

        if (this.storeAuth.user.no_shipping_costs === 1 || this.storeAuth.user.no_shipping_costs === '1') {
            isOverruled = true;
        };

        return isOverruled;
    }

    checkNoShippingOrder = async(orderId) => {
        let noShipping = false;

        try {
            const returnData = await order.checkNoShippingOrder(orderId);
            runInAction(() => {
                const value = returnData.data[0].no_shipping_costs;

                if (value === 1) {
                    noShipping = true;
                } 

                return noShipping;
            })
        } catch (error) {
            runInAction(() => {
                this.state = "error"
            });
            throw error;
        }

        return noShipping;
    }

    checkNoShippingSku(orderId) {
        let noShipping = false;
        let items = [];
        const loggedin = this.storeOrder.order_data.client_id !== 0 ? true : false;

        const list = [
            '9789492665508', //DE
            '9789492665317' //EN
        ];

        if (loggedin) {
            items = this.storeOrder.cart.user;
        } else {
            items = this.storeOrder.cart.anonymous;
        }

        items.map((product) => {
            if (list.includes(product.sku) && parseInt(product.amount) !== 0 && product.status !== 'deletedx') {
               noShipping = true;
            }
        });

        return noShipping;
    }

    checkShippingOrderOverruled(orderId) {
        let isOverruled = false;

        if (this.storeOrder.order_settings.custom_shipping_costs === 1) {
           isOverruled = true;
        }

        return isOverruled;
    }

    // getShippingCustomerOverruled(userId) {
    //     let shipping;

    //     if (this.storeAuth.user.no_shipping_costs === 1 || this.storeAuth.user.no_shipping_costs === '1') {
    //         shipping = 0;
    //     };

    //     return shipping;
    // }
    

    checkFreeShippingBoundaries(orderId, shipping) {
        let evaluatedShipping = shipping;
        let totalCart = 0;
        let discountApplied = false;
        const configuredFreeThreshold = Number(this.storeOrder.order_settings.shipping_free);
        const configuredResellerThreshold = Number(this.storeOrder.order_settings.shipping_free_resellers);
        const freeThreshold = Number.isFinite(configuredFreeThreshold) && configuredFreeThreshold > 0
            ? configuredFreeThreshold
            : 20;
        const resellerThreshold = Number.isFinite(configuredResellerThreshold) && configuredResellerThreshold > 0
            ? configuredResellerThreshold
            : 250;

        let items = [];
        const loggedin = this.storeOrder.order_data.client_id !== 0 ? true : false;
        if (loggedin) {
            items = this.storeOrder.cart.user;
        } else {
            items = this.storeOrder.cart.anonymous;
        }

        items.map((product) => {
            if (product.status !== 'deletedx' && product.amount > 0) {
                discountApplied = (product.discount > 0) ? true : discountApplied; 
                discountApplied = (product.discount > 0 && this.storeOrder.order_settings.discounts_overruled === 1) ? true : discountApplied;
                
                totalCart = (discountApplied) 
                    ?   totalCart + parseFloat(product.price)  * ((100 - product.discount)/100) * parseFloat(product.amount)
                    :   totalCart + parseFloat(product.price)  * parseFloat(product.amount)
            }
        })

        if (totalCart <= 0) {
            evaluatedShipping = 0;
        }

        if (!discountApplied && totalCart >= freeThreshold) {
            evaluatedShipping = 0;
        }

        //SbC free above 250 for all
        if (totalCart >= resellerThreshold) {
            evaluatedShipping = 0;
        }

        return evaluatedShipping;
    }

    getShippingOrderOverruled = async(orderId) => {
        let shipping;

        if (this.storeOrder.order_settings.custom_shipping_costs === '1' || this.storeOrder.order_settings.custom_shipping_costs === 1) {
            shipping = await this.getCustomShippingCosts(orderId);
        }

        return shipping;
    }

    getCustomShippingCosts = async(orderId) => {
        let custom_shipping = 0;
        try {
            const returnData = await order.getCustomShippingCosts(orderId);
            runInAction(() => {              
                custom_shipping = parseFloat(returnData.data[0].custom_shipping_costs) / (1 + parseFloat(returnData.data[0].shipping_vat) / 100);

                return custom_shipping;
            })
        } catch (error) {
            runInAction(() => {
                this.state = "error"
            });
            throw error;
        }
        return custom_shipping;
    }

    getWeight() {
        let weight = this.calculateWeight;

        return weight;
    }

    getPackageType(weight) {
        // SbC default is as_letter
        let packageType = 2; 

        let items = [];
        const loggedin = this.storeOrder.order_data.client_id !== 0 ? true : false;
        if (loggedin) {
            items = this.storeOrder.cart.user;
        } else {
            items = this.storeOrder.cart.anonymous;
        }

        items.map((product) => {
            //SbC check if package type is overruled for bottles etc
            if (product.package_type === 1 || product.weight === 1) {
                packageType = 1;
            }
        });

        if (items.length >= 3 && weight > 1000) {
            packageType = 1;
        }

        if (weight > 1000) {
            packageType = 1;
        }

        return packageType;
    }

    getShippingCosts = async(orderId, weight, packageType) => {
        //SbC arbitrary default value
        let shipping;
        let shippingData;

        let returnData;
        try { 
            //SbC always based on weight
            //SbC CHECK what if digital products only like MP3 of event ticket?

            //if (orderId === 0 || orderId === '0' || parseInt(orderId) === 99) {
                returnData = await tools.getShippingCosts(weight);
            // } else {
            //SbC DEPRICATED as with new order in review first sometimes no orderlines yet, resulting in only package weight of 80
            //     returnData = await tools.getShippingCostsV2(orderId);
            //     console.log('SbC return shipping data v2', weight, orderId, returnData.data[0])
            // }
            shippingData = returnData?.data?.[0];
            if (!shippingData || shippingData.as_package == null) throw new Error('Verzendtarief niet beschikbaar.');

            runInAction(async() => {
                if (shippingData) { 
                    shipping = (shippingData.as_letter !== null && packageType !== 1)
                    ?   shippingData.as_letter <= shippingData.as_package 
                        ?   shippingData.as_letter 
                        :   shippingData.as_package
                    :   shippingData.as_package
                }
            })
        } catch (error) {
            runInAction(() => {
                this.state = "error"
            });
            throw error;
        }   

        return shipping;
    }

    getShippingCostsAbroad = async(weight, countryCode = this.getShippingCountryCode()) => {
        let shipping;
        let surcharge = 0;

        const countriesList = this.storeOrder.countriesList;
        const idx = countriesList.findIndex(country => String(country.code).toUpperCase() === countryCode);

        //SbC calculate weight surcharge if applicable
        if (idx !== -1) {
            shipping = Number(countriesList[idx].base_shipping);
        
            if (countriesList[idx].surcharge_weight !== null && countriesList[idx].surcharge_weight !== '') {
                surcharge = weight / 1000 * Number(countriesList[idx].surcharge_weight);
                shipping = shipping + surcharge;
            }
        }

        if (!Number.isFinite(shipping)) {
            throw new Error('Geen verzendtarief gevonden voor ' + countryCode + '.');
        }

        return shipping;
    }

    calculateShippingVat() {
        let items = [];
        const loggedin = this.storeOrder.order_data.client_id !== 0 ? true : false;
        if (loggedin) {
            items = this.storeOrder.cart.user;
        } else {
            items = this.storeOrder.cart.anonymous;
        }
        
        let vat_perc = 9;
        items.map((product) => {
            if (product.status !== 'deletedx' && parseInt(product.amount) !== 0 && product.vat_perc > vat_perc) {
                vat_perc = product.vat_perc;
            }
        })

        return vat_perc;
    }

    get calculateWeight() {
        let weight = 0;
        let weight_box = 80;
        if (this.storeOrder.settings_list["weightBox"] !== undefined) {
            weight_box = parseInt(this.storeOrder.settings_list["weightBox"]);
        }

        let items = [];
        const loggedin = this.storeOrder.order_data.client_id !== 0 ? true : false;
        if (loggedin) {
            items = this.storeOrder.cart.user;
        } else {
            items = this.storeOrder.cart.anonymous;
        }

        items.map((product, i) => {
            if (product.status !== 'deletedx' && product.amount > 0) {
                if (product.weight === null || product.weight === '') {
                    //SbC to prevent miscalculation in case no weight set
                    
                    let defaultWeight = 300;
                    weight = weight + defaultWeight * Number(product.amount);
                } else {
                    weight = weight + product.amount * parseInt(product.weight);
                }
                
                return weight;
            }
        })

        // A cart containing only downloads/tickets has no parcel to package.
        return weight > 0 ? weight + weight_box : weight;
    }
}

decorate(StoreShipping, {
    calculateShipping: action,
    calculateWeight: computed
})

export default StoreShipping;
